/**
 * UI theme — palette, easing curves and canvas helpers.
 * Owner: ui. Everything the HUD draws pulls its colours and curves from here so
 * the whole overlay reads as one designed system.
 */

/* Restrained palette. Neutral bone-white primary, one amber accent, one cyan
 * friendly, one red hostile. Nothing else earns a hue. */
export const COL = {
  white: '#EDF2F4',
  bone: '#D6DDE1',
  dim: '#93A0A7',
  faint: '#5B676D',
  ghost: 'rgba(237,242,244,0.28)',

  amber: '#FFC24B',
  amberDeep: '#E09317',
  ally: '#5FD2FF',
  allyDeep: '#1E86B8',
  enemy: '#FF4438',
  enemyDeep: '#B21F16',
  kill: '#FF3B2C',
  good: '#79E06B',

  ink: '#04070A',
  shadow: 'rgba(0,0,0,0.62)',
  shadowSoft: 'rgba(0,0,0,0.38)',
  panel: 'rgba(7,10,13,0.74)',
  panelHi: 'rgba(18,24,29,0.86)',
  hair: 'rgba(237,242,244,0.16)',
  hairHi: 'rgba(237,242,244,0.34)',
};

/** System stack only — no webfonts (hard constraint). Numerals are drawn by hand. */
export const FONT =
  '"Helvetica Neue", "Arial Narrow", Arial, "Segoe UI", system-ui, sans-serif';

/* ---------------------------------------------------------------- easing */

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a, b, t) => a + (b - a) * t;
export const smoothstep = (e0, e1, x) => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};
/** Frame-rate independent exponential approach. `rate` = 1/e time in seconds. */
export const approach = (cur, target, rate, dt) =>
  cur + (target - cur) * (1 - Math.exp(-dt / Math.max(1e-5, rate)));

export const ease = {
  outCubic: (t) => 1 - Math.pow(1 - t, 3),
  outQuart: (t) => 1 - Math.pow(1 - t, 4),
  outQuint: (t) => 1 - Math.pow(1 - t, 5),
  outExpo: (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -11 * t)),
  inOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  /** Overshoots by ~11 % then settles — the "snap" curve used for every punch. */
  outBack: (t) => {
    const c1 = 1.9, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  /** Hard mechanical snap: fast start, tiny settle. */
  snap: (t) => 1 - Math.pow(1 - t, 6),
};

/* ---------------------------------------------------------------- canvas */

export function devicePx() {
  return Math.min(window.devicePixelRatio || 1, 2);
}

/**
 * Size a canvas to CSS dimensions with a device-pixel backing store and return
 * its 2D context pre-scaled so all drawing happens in CSS pixels while landing
 * on exact device pixels.
 */
export function fitCanvas(cv, cssW, cssH, dpr = devicePx()) {
  const w = Math.round(cssW * dpr);
  const h = Math.round(cssH * dpr);
  if (cv.width !== w || cv.height !== h) {
    cv.width = w;
    cv.height = h;
  }
  cv.style.width = cssW + 'px';
  cv.style.height = cssH + 'px';
  const g = cv.getContext('2d', { alpha: true, desynchronized: true });
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.__dpr = dpr;
  g.__w = cssW;
  g.__h = cssH;
  return g;
}

export function clearCanvas(g) {
  g.save();
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, g.canvas.width, g.canvas.height);
  g.restore();
}

/** Snap a CSS-px coordinate so a `w`-wide stroke lands on whole device pixels. */
export function snapPx(v, w, dpr = devicePx()) {
  const d = Math.round(w * dpr);
  const half = (d % 2) / 2;
  return (Math.round(v * dpr - half) + half) / dpr;
}

/** Crisp axis-aligned rect: rounds edges to the device-pixel grid. */
export function crispRect(g, x, y, w, h, dpr = g.__dpr || devicePx()) {
  const x0 = Math.round(x * dpr) / dpr;
  const y0 = Math.round(y * dpr) / dpr;
  const x1 = Math.round((x + w) * dpr) / dpr;
  const y1 = Math.round((y + h) * dpr) / dpr;
  g.fillRect(x0, y0, Math.max(1 / dpr, x1 - x0), Math.max(1 / dpr, y1 - y0));
}

/** Rect with a dark 1 px halo so it survives a bright sky behind it. */
export function outlinedRect(g, x, y, w, h, fill, halo = COL.shadow, o = 1) {
  g.fillStyle = halo;
  crispRect(g, x - o, y - o, w + o * 2, h + o * 2);
  g.fillStyle = fill;
  crispRect(g, x, y, w, h);
}

/** Chamfered plate path (the HUD's signature corner treatment). */
export function chamferPath(g, x, y, w, h, c = 6, corners = 0b1111) {
  const tl = corners & 0b1000 ? c : 0;
  const tr = corners & 0b0100 ? c : 0;
  const br = corners & 0b0010 ? c : 0;
  const bl = corners & 0b0001 ? c : 0;
  g.beginPath();
  g.moveTo(x + tl, y);
  g.lineTo(x + w - tr, y);
  g.lineTo(x + w, y + tr);
  g.lineTo(x + w, y + h - br);
  g.lineTo(x + w - br, y + h);
  g.lineTo(x + bl, y + h);
  g.lineTo(x, y + h - bl);
  g.lineTo(x, y + tl);
  g.closePath();
}

export function plate(g, x, y, w, h, opt = {}) {
  const c = opt.cut ?? 6;
  chamferPath(g, x, y, w, h, c, opt.corners ?? 0b1111);
  if (opt.fill !== null) {
    g.fillStyle = opt.fill || COL.panel;
    g.fill();
  }
  if (opt.stroke) {
    g.lineWidth = opt.strokeWidth ?? 1;
    g.strokeStyle = opt.stroke;
    g.stroke();
  }
}

/** hex → rgba() with alpha. Cached; used on every frame path. */
const _rgbaCache = new Map();
export function rgba(hex, a) {
  // Quantise alpha so a continuously-animating value cannot churn the cache.
  a = Math.round(a * 255) / 255;
  const key = hex + '|' + a;
  let v = _rgbaCache.get(key);
  if (v) return v;
  let r, g, b;
  if (hex[0] === '#') {
    const n = parseInt(hex.slice(1), 16);
    r = (n >> 16) & 255; g = (n >> 8) & 255; b = n & 255;
  } else {
    const m = hex.match(/[\d.]+/g);
    r = +m[0]; g = +m[1]; b = +m[2];
  }
  v = `rgba(${r},${g},${b},${a})`;
  if (_rgbaCache.size < 4000) _rgbaCache.set(key, v);
  return v;
}
